import { createContext, useContext, useState, useEffect, useCallback, useRef, useMemo } from 'react'
import { adminApi } from '../services/ApiService'
import { useAuth } from './AuthContext'

const AdminDataContext = createContext()

const REFRESH_INTERVAL = 60_000 // 60s auto-refresh

export function AdminDataProvider({ children }) {
  const { isReseller } = useAuth()
  const [devices, setDevices] = useState([])
  const [health, setHealth] = useState(null)
  const [subscriptions, setSubscriptions] = useState(null)
  const [alerts, setAlerts] = useState([])
  const [loading, setLoading] = useState(!isReseller)
  const [lastUpdate, setLastUpdate] = useState(null)
  const fetchingRef = useRef(false)

  const fetchOverview = useCallback(async (silent = false) => {
    if (isReseller) return
    if (fetchingRef.current) return
    fetchingRef.current = true
    if (!silent) setLoading(true)
    try {
      const data = await adminApi.get('/admin/overview')
      if (data.devices) setDevices(data.devices.devices || [])
      if (data.health) setHealth(data.health)
      if (data.subscriptions) setSubscriptions(data.subscriptions)
      if (data.alerts) setAlerts(data.alerts.alerts || [])
      setLastUpdate(new Date())
    } catch (err) {
      console.error('AdminDataContext fetch error:', err)
    } finally {
      fetchingRef.current = false
      if (!silent) setLoading(false)
    }
  }, [isReseller])

  // Fetch on mount + auto-refresh
  useEffect(() => {
    fetchOverview()
    const interval = setInterval(() => fetchOverview(true), REFRESH_INTERVAL)
    return () => clearInterval(interval)
  }, [fetchOverview])

  const refresh = useCallback(() => fetchOverview(false), [fetchOverview])

  const value = useMemo(() => ({
    devices,
    health,
    subscriptions,
    alerts,
    loading,
    lastUpdate,
    refresh,
  }), [devices, health, subscriptions, alerts, loading, lastUpdate, refresh])

  return (
    <AdminDataContext.Provider value={value}>
      {children}
    </AdminDataContext.Provider>
  )
}

export function useAdminData() {
  const ctx = useContext(AdminDataContext)
  if (!ctx) throw new Error('useAdminData debe usarse dentro de AdminDataProvider')
  return ctx
}
